import { useUiStore } from '../../store/useUiStore';

// --- Download cache helpers (session-persistent via Cache Storage) ---
const FILE_CACHE_NAME = 'ef-file-cache-v2';
async function cacheHas(url) {
  try {
    if (!('caches' in window)) return false;
    const c = await window.caches.open(FILE_CACHE_NAME);
    const m = await c.match(url, { ignoreVary: true, ignoreSearch: false });
    return !!m;
  } catch { return false; }
}
async function cachePut(url, response) {
  try {
    if (!('caches' in window)) return;
    const c = await window.caches.open(FILE_CACHE_NAME);
    await c.put(url, response);
  } catch {}
}
async function cacheOpenBlobUrl(url) {
  try {
    if (!('caches' in window)) return null;
    const c = await window.caches.open(FILE_CACHE_NAME);
    const m = await c.match(url, { ignoreVary: true, ignoreSearch: false });
    if (!m) return null;
    const blob = await m.blob();
    return URL.createObjectURL(blob);
  } catch { return null; }
}

// --- Infer content type from file name (for download/open fixes) ---
function inferMimeFromName(name) {
  if (!name) return 'application/octet-stream';
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'txt') return 'text/plain';
  if (ext === 'csv') return 'text/csv';
  if (ext === 'json') return 'application/json';
  if (ext === 'doc' || ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (ext === 'xls' || ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'ppt' || ext === 'pptx') return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  if (ext === 'zip') return 'application/zip';
  if (ext === 'mp3') return 'audio/mpeg';
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'svg') return 'image/svg+xml';
  return 'application/octet-stream';
}

// --- Human-readable file size formatter ---
function formatBytesHuman(value) {
  if (value == null) return '';
  // If it's already a string with units, return as-is
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/\d\s?(B|KB|MB|GB|TB)$/i.test(trimmed)) return trimmed;
    const asNum = Number(trimmed);
    if (!Number.isFinite(asNum)) return trimmed;
    value = asNum; // fall through to numeric formatting
  }
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return String(value);
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  const rounded = n >= 100 ? Math.round(n) : (n >= 10 ? Math.round(n * 10) / 10 : Math.round(n * 100) / 100);
  return `${rounded} ${units[i]}`;
}

// Get a blob URL from cache; if cached type is octet-stream but we can guess a better one,
// re-cache with the corrected type so browsers render inline instead of downloading.
async function cacheOpenBlobUrlWithType(url, fallbackName) {
  try {
    if (!('caches' in window)) return { blobUrl: null, contentType: null };
    const c = await window.caches.open(FILE_CACHE_NAME);
    let m = await c.match(url, { ignoreVary: true, ignoreSearch: false });
    if (!m) return { blobUrl: null, contentType: null };
    let ct = m.headers.get('Content-Type') || 'application/octet-stream';
    if (ct === 'application/octet-stream') {
      const guessed = inferMimeFromName(fallbackName);
      if (guessed && guessed !== 'application/octet-stream') {
        const b = await m.blob();
        const fixed = new Response(b, { headers: { 'Content-Type': guessed } });
        await c.put(url, fixed);
        m = fixed;
        ct = guessed;
        try { console.debug('[Attachment] cache content-type fixed', { url, guessed }); } catch {}
      }
    }
    const blob = await m.blob();
    return { blobUrl: URL.createObjectURL(blob), contentType: ct };
  } catch {
    return { blobUrl: null, contentType: null };
  }
}
import React, { useMemo, useCallback } from 'react';
// For map bubble sizing (fixed width, aspect ratio)
const BUBBLE_WIDTH = Math.min(340, Math.floor((typeof window !== "undefined" ? window.innerWidth : 400) * 0.8));
const MAP_ASPECT = 9 / 16;
const MAP_HEIGHT = Math.round(BUBBLE_WIDTH * MAP_ASPECT);
// In-memory cache of successful static map URLs keyed by (lat,lng,gKey)
const __staticMapCache = new Map();
const cacheKeyFor = (lat, lng, gKey) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}|${gKey ? String(gKey).slice(0,6) : ''}`;
// Persist cache for the session so refreshes reuse known-good URLs
const SS_CACHE_KEY = '__staticMapCache_v1';
const canUseSession = () => (typeof window !== 'undefined' && !!window.sessionStorage);
const loadSessionCache = () => {
  if (!canUseSession()) return;
  try {
    const raw = window.sessionStorage.getItem(SS_CACHE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') {
      Object.entries(obj).forEach(([k, v]) => { if (typeof v === 'string') __staticMapCache.set(k, v); });
    }
  } catch {}
};
const saveSessionCache = () => {
  if (!canUseSession()) return;
  try {
    // Cap at ~30 entries to avoid bloat
    while (__staticMapCache.size > 30) {
      const firstKey = __staticMapCache.keys().next().value;
      if (!firstKey) break;
      __staticMapCache.delete(firstKey);
    }
    const obj = {};
    for (const [k, v] of __staticMapCache.entries()) obj[k] = v;
    window.sessionStorage.setItem(SS_CACHE_KEY, JSON.stringify(obj));
  } catch {}
};
// Load any prior session cache immediately
try { loadSessionCache(); } catch {}
// NOTE: Concierge (hotel) side treats entry.hotel as "mine" for reactions toggle/highlight.
import { Box, Typography, Avatar, Stack, Link as MuiLink, useTheme, Dialog, DialogContent, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Download as IconDownload, FileText as IconFileText } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useConciergeStore } from '../../store/conciergeStore';




import ContextMenu from './ContextMenu';
import MapViewModal from './MapViewModal';
import { getSignedAttachmentUrlFor } from '../../api/conciergeApi';

// --- role helpers (back-compat with legacy 'guest'|'hotel') ---
function roleOf(sender) {
  if (!sender) return undefined;
  if (typeof sender === 'string') return sender === 'guest' ? 'guest' : 'staff';
  if (typeof sender === 'object') return sender.role || undefined;
  return undefined;
}
function isGuestSender(sender) {
  return roleOf(sender) === 'guest';
}
function isStaffSender(sender) {
  return roleOf(sender) === 'staff';
}
// Helper to detect if attachment is a map/location
const isMapAtt = (a) => {
  if (!a) return false;
  const t = (a?.type || a?.kind || '').toLowerCase();
  const url = String(a?.url || a?.link || '');
  const hasCoords = (a?.lat != null && a?.lng != null) || (a?.latitude != null && a?.longitude != null);
  return t.startsWith('location') || t.startsWith('map') || hasCoords || /maps\.google\.com|geo:|apple\.maps/i.test(url);
};

// Parse a "map-like" block from plain text (lines: title, address, "lat,lng", google maps URL)
const parseMapFromText = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!/maps\.google\.com/i.test(text) && !/(-?\d{1,2}\.\d{3,}),\s*(-?\d{1,3}\.\d{3,})/.test(text)) return null;
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;
  let title = lines[0];
  let address = '';
  let lat = null, lng = null, url = '';
  // Find coords line
  for (const l of lines) {
    const m = l.match(/(-?\d{1,2}\.\d{3,}\d*),\s*(-?\d{1,3}\.\d{3,}\d*)/);
    if (m) { lat = parseFloat(m[1]); lng = parseFloat(m[2]); break; }
  }
  // Find maps url line
  for (const l of lines) {
    if (/maps\.google\.com/i.test(l)) { url = l; break; }
  }
  // Pick address as the first non-title/non-url/non-coords line
  for (let i = 1; i < lines.length; i++) {
    const l = lines[i];
    if (l === url) continue;
    if (/-?\d/.test(l) && l.includes(',')) continue; // skip coords if mistaken
    address = l;
    break;
  }
  if (!url && lat != null && lng != null) url = `https://maps.google.com/?q=${lat},${lng}`;
  if (!url && (lat == null || lng == null)) return null; // not confident
  return { title, address, lat, lng, url };
};

function MapAttachment({ att, msg, isHotel, isBusy, onOpenMap }) {
  const name = att?.name || att?.title || 'Pinned location';
  const address = att?.address || att?.subtitle || '';
  const lat = att?.lat ?? att?.latitude;
  const lng = att?.lng ?? att?.longitude;
  const url = att?.url || att?.link || (lat != null && lng != null ? `https://maps.google.com/?q=${lat},${lng}` : null);
  const chipBg = isHotel ? 'rgba(255,255,255,0.92)' : '#F2F4F7';
  const gold = '#C6A667'; // Eightfold gold
  // CRA env (React-Scripts): use REACT_APP_* keys; allow runtime override via window for quick dev testing
  const gKey = String(
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GOOGLE_STATIC_MAPS_KEY) ||
    (typeof window !== 'undefined' && window.__GOOGLE_STATIC_MAPS_KEY) ||
    ''
  );
  const mbx = String(
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MAPBOX_TOKEN) ||
    (typeof window !== 'undefined' && window.__MAPBOX_TOKEN) ||
    ''
  );
  // const MAP_HEIGHT = 180; // fixed height to keep map bubbles consistent
  // const MAP_AR = '16 / 9'; // consistent aspect ratio for static map preview
  // Stable cache-buster so the second Google candidate doesn't change every render
  const cbStableRef = React.useRef('');
  React.useEffect(() => {
    // regenerate only when location or key changes
    cbStableRef.current = String(Math.floor(Math.random() * 1e6)).padStart(6, '0');
  }, [lat, lng, gKey]);
  const candidates = React.useMemo(() => {
    const list = [];
    if (lat != null && lng != null) {
      // Prefer a previously successful URL for this location/key
      const ck = cacheKeyFor(lat, lng, gKey);
      const cached = __staticMapCache.get(ck);
      if (cached) list.push(cached);

      if (gKey) {
        const usp = new URLSearchParams({
          center: `${lat},${lng}`,
          zoom: '16',
          size: '640x360', // matches 16:9 aspect ratio
          scale: '2',
          maptype: 'roadmap',
          markers: `color:red|${lat},${lng}`,
          key: gKey,
        });
        list.push(`https://maps.googleapis.com/maps/api/staticmap?${usp.toString()}`);
        // Add a cache-busted retry candidate (stable per (lat,lng,gKey))
        const cb = cbStableRef.current;
        list.push(`https://maps.googleapis.com/maps/api/staticmap?${usp.toString()}&cb=${cb}`);
      }
      if (mbx) {
        const pin = `pin-l+ff0000(${lng},${lat})`;
        const style = 'light-v11';
        list.push(`https://api.mapbox.com/styles/v1/mapbox/${style}/static/${pin}/${lng},${lat},16,0/640x360@2x?access_token=${mbx}`);
      }
      if (!gKey && !mbx) {
        const usp2 = new URLSearchParams({
          center: `${lat},${lng}`,
          zoom: '16',
          size: '640x360',
          markers: `${lat},${lng},red-pushpin`,
        });
        list.push(`https://staticmap.openstreetmap.de/staticmap.php?${usp2.toString()}`);
      }
    }
    return list;
  }, [lat, lng, gKey, mbx]);
  // Debug: show provider queue
  // Robust loader state
  const [loadedSrc, setLoadedSrc] = React.useState(null);
  const [loadState, setLoadState] = React.useState('idle'); // idle | loading | loaded | failed
  const MAP_TIMEOUT_MS = 10000;      // more forgiving than very short timeouts
  const MAX_RETRIES_PER_URL = 2;     // retry each candidate once
  const attemptRef = React.useRef(0); // guard against stale onload/onerror

  // Reset when inputs change
  React.useEffect(() => {
    setLoadedSrc(null);
    setLoadState('idle');
  }, [lat, lng, gKey, mbx]);

  // Try to load candidates with retries and timeout; ignore stale completions
  React.useEffect(() => {
    if (!candidates.length) { setLoadState('failed'); return; }
    let cancelled = false;
    const myAttempt = ++attemptRef.current;

    async function loadWithTimeout(url, timeoutMs) {
      return await new Promise((resolve) => {
        const img = new window.Image();
        let timer = setTimeout(() => {
          img.onload = img.onerror = null;
          resolve(false);
        }, timeoutMs);
        img.onload = () => { clearTimeout(timer); resolve(true); };
        img.onerror = () => { clearTimeout(timer); resolve(false); };
        img.referrerPolicy = 'strict-origin-when-cross-origin';
        img.decoding = 'async';
        img.loading = 'eager';
        img.src = url;
      });
    }

    async function tryLoad() {
      setLoadState('loading');
      for (let i = 0; i < candidates.length && !cancelled; i++) {
        const url = candidates[i];
        for (let r = 0; r < MAX_RETRIES_PER_URL && !cancelled; r++) {
          const ok = await loadWithTimeout(url, MAP_TIMEOUT_MS * (r ? 1.5 : 1));
          if (cancelled || myAttempt !== attemptRef.current) return; // stale
          if (ok) {
            // cache successful URL per (lat,lng,gKey)
              try {
                const ck = cacheKeyFor(lat, lng, gKey);
                __staticMapCache.set(ck, url);
                saveSessionCache();
              } catch {}
            setLoadedSrc(url);
            setLoadState('loaded');
            return;
          }
        }
      }
      if (!cancelled && myAttempt === attemptRef.current) setLoadState('failed');
    }

    tryLoad();
    return () => { cancelled = true; };
  }, [candidates, lat, lng, gKey, mbx]);

  // Prefer an address under the title. If attachment lacks a clean address, try to derive from the message text.
  const addressFromText = React.useMemo(() => {
    const raw = (msg?.text || msg?.content || msg?.message || msg?.body || '').trim();
    if (!raw) return '';
    const lines = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length < 2) return '';
    const http = /^https?:\/\//i;
    const coord = /^-?\d{1,2}\.\d+\s*,\s*-?\d{1,3}\.\d+$/;
    // Heuristic: the first non-URL, non-coordinate line after the title that looks like an address
    for (let i = 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (!http.test(line) && !coord.test(line) && line.length > 6 && line.includes(',')) return line;
    }
    return '';
  }, [msg?.text, msg?.content, msg?.message, msg?.body]);
  // --- Map Bubble UI: fixed width, aspect, inner padding ---
  // Use React Native style for sizing, but in web, adapt with inline style.
  // Use a <div> for fixed width, border radius, overflow, background.
  // Title: 1 line clamp, address: 2 line clamp.
  return (
    <div
      style={{
        width: BUBBLE_WIDTH,
        borderRadius: 14,
        overflow: "hidden",
        backgroundColor: "#fff",
        opacity: isBusy ? 0.6 : 1,
        pointerEvents: isBusy ? "none" : "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      {/* Static map preview */}
      {loadState !== "failed" && (loadedSrc || loadState === "loading") ? (
        <div
          onClick={() => {
            if (onOpenMap) {
              onOpenMap({ name, title: name, address, lat, lng, latitude: lat, longitude: lng, url });
            } else if (url) {
              window.open(url, "_blank");
            }
          }}
          style={{ width: "100%", cursor: "pointer", lineHeight: 0 }}
          role="button"
          aria-label={`Open map for ${name}`}
        >
          <img
            src={loadedSrc || candidates[0]}
            alt={name}
            referrerPolicy="strict-origin-when-cross-origin"
            decoding="async"
            loading="lazy"
            style={{
              width: "100%",
              height: MAP_HEIGHT,
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: MAP_HEIGHT,
            background: chipBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: "#6B7280", fontSize: 15 }}>Map preview unavailable</span>
        </div>
      )}
      {/* Caption */}
      <div style={{ padding: "10px 12px" }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={name}
        >
          {name}
        </div>
        {(() => {
          const plusCodeRe = /\b[A-Z0-9]{4,}\+[A-Z0-9]{2,}\b/;
          const cleanAttAddr = address && !plusCodeRe.test(String(address)) ? String(address) : "";
          const candidate = cleanAttAddr || addressFromText;
          if (candidate) {
            return (
              <div
                style={{
                  fontSize: 13,
                  lineHeight: "18px",
                  opacity: 0.8,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  maxHeight: 38,
                  marginTop: 2,
                }}
                title={candidate}
              >
                {String(candidate).length > 160 ? candidate.slice(0, 157) + "…" : candidate}
              </div>
            );
          }
          // Fallback: show coordinates only when there is no clean address
          if (lat != null && lng != null) {
            return (
              <div style={{ fontSize: 13, color: "#374151", marginTop: 2 }}>
                {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
              </div>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
}

// Treat only real emoji-like strings as valid for rendering (supports map or array entry)
const isRenderableReaction = (emojiKey, entry) => {
  // If the key looks numeric (array index), fall back to entry.emoji
  let e = '';
  if (typeof emojiKey === 'string' && emojiKey.trim() && isNaN(Number(emojiKey))) {
    e = emojiKey.trim();
  } else if (entry && typeof entry.emoji === 'string') {
    e = entry.emoji.trim();
  }
  if (!e) return false;
  if (e === '0' || e === 'null' || e === 'undefined') return false;

  const count = Number(entry?.count || 0);
  const mine = !!entry?.hotel; // present in map shape
  const other = !!entry?.guest; // present in map shape
  return mine || other || count > 0;
};


// Helper to detect if attachment is an image

/**
 * ConciergeBubble
 * Lightweight message bubble used by ConciergeMessageList
 *
 * Props:
 * - msg: {
 * sender: 'guest' | 'hotel',
 * content?: string,
 * text?: string,
 * attachments?: Array<{ url: string, type?: string, name?: string }>,
 * timestamp?: string | number | Date,
 * createdAt?: string | number | Date,
 * seen?: boolean
 * }
 * - showAvatar?: boolean (default false)
 * - onQuotedClick?: function (optional)
 */
export default function ConciergeBubble({ msg, showAvatar = false, onQuotedClick, isSelected = false, onSelect }) {
  const theme = useTheme();
  const isHotel = isStaffSender(msg?.sender) || msg?.fromGuest === false; // staff == me (back-compat: fromGuest===false)
  const align = isHotel ? 'flex-end' : 'flex-start';
  // Google Maps browser key (from env or window for dev)
  const gKey = String(
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GOOGLE_STATIC_MAPS_KEY) ||
    (typeof window !== 'undefined' && window.__GOOGLE_STATIC_MAPS_KEY) ||
    ''
  );

  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  const openLightbox = (src) => setLightboxSrc(src || null);
  const closeLightbox = () => setLightboxSrc(null);

  // Map view modal state
  const [mapModal, setMapModal] = React.useState(null); // { name, title, address, lat, lng, url }
  const openMapModal = React.useCallback((payload) => {
    const p = payload || null;
    const normalized = p ? { ...p, lat: p.lat ?? p.latitude, lng: p.lng ?? p.longitude } : null;
    try { console.debug('[ConciergeBubble] openMapModal', normalized); } catch {}
    setMapModal(normalized);
  }, []);
  const closeMapModal = React.useCallback(() => setMapModal(null), []);

  // Hotel (me) stays gold; Guest side becomes light gray
  const bubbleBg = isHotel ? '#ddeff75f' : '#F1F1F1';
  const bubbleText = isHotel ? '#0F172A' : '#111827';
  const shadow = '0 3px 8px rgba(0, 0, 0, 0.17)';
  const corner = 18;

  const isImageAtt = (a) => {
    if (!a) return false;
    const t = String(a.type || a.mime || '').toLowerCase();
    const name = String(a.name || '');
    const u = String(a.url || '');
    const ext = (name.split('.').pop() || '').toLowerCase();
    // Explicitly exclude PDFs even if a thumbnailUrl exists
    const isPdf = t === 'application/pdf' || ext === 'pdf' || /\.pdf($|\?)/i.test(u);
    if (isPdf) return false;
    // Positive checks for real image types/extensions
    const looksImageType = t.startsWith('image/') || t === 'image';
    const looksImageExt = /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(u) || /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(name);
    // If server gave us a thumbnail, allow as image unless it's a PDF we already excluded
    const hasThumb = !!a.thumbnailUrl;
    return looksImageType || looksImageExt || hasThumb;
  };

  const textRaw =
    (typeof msg?.content === 'string' && msg.content) ||
    (typeof msg?.text === 'string' && msg.text) ||
    (typeof msg?.message === 'string' && msg.message) ||
    (typeof msg?.body === 'string' && msg.body) ||
    '';
  let text = String(textRaw || '');
  const attachments = useMemo(() => Array.isArray(msg?.attachments) ? msg.attachments : [], [msg?.attachments]);
  // Normalize placeholder/whitespace-only text for attachment-only messages
  if (Array.isArray(msg?.attachments) && msg.attachments.length > 0) {
    const t = text.trim();
    if (!t || t.toLowerCase() === '(attachment)') {
      text = '';
    }
  }
  const parsedMap = useMemo(() => parseMapFromText(text), [text]);
  const canCopyText = useMemo(() => (text && text.trim().length > 0), [text]);
  const hasSingleImage = useMemo(() => attachments.length === 1 && isImageAtt(attachments[0]), [attachments]);
  const isMapOnly = !!parsedMap && attachments.length === 0;
  const isImageOnly = attachments.length > 0 && attachments.every(isImageAtt) && text.trim().length === 0 && !parsedMap;
  const time = formatTime(msg?.timestamp ?? msg?.createdAt ?? msg?.time);
  const timeTitle = (() => {
    const v = (msg?.timestamp ?? msg?.createdAt ?? msg?.time);
    if (!v) return '';
    try { return new Date(v).toLocaleString(); } catch { return String(v); }
  })();
  // Accept multiple back-end field names for safety

  if (!text && attachments.length === 0) {
  }
  const imageCount = attachments.filter(isImageAtt).length;
  const showImageGrid = imageCount > 1 && imageCount === attachments.length;
  const isSending = !!msg?.sending && !msg?.error;
  const isFailed = !!msg?.error;
  const isUpdating = !!msg?.updating;   // e.g., during attachment deletion
  const isDeleting = !!msg?.deleting;   // whole-message deletion in progress
  const isBusy = isSending || isUpdating || isDeleting;

  // --- Read receipts (Seen) ---
  // Support both legacy boolean and modern per-side shape: seenBy: [{role:'guest'|'staff', id?, at?}]
  const seenByArr = Array.isArray(msg?.seenBy) ? msg.seenBy : [];
  const seenByGuest = seenByArr.some((s) => s && (s.role === 'guest' || s.role === 'user' || s.role === 'client'));
  const legacySeen = !!msg?.seen;
  // Only show for my (hotel/staff) outgoing bubbles; hide while sending/failed/deleting
  const isSeenReceipt = isHotel && (legacySeen || seenByGuest) && !isSending && !isFailed && !isDeleting;
  const retrySend = useConciergeStore(s => s.retrySend);
  const setReplyTarget = useConciergeStore(s => s.setReplyTarget);
  const activeThread = useConciergeStore(s => s.activeThread);

  // --- UI focus + context menu state ---
  const focusedMessageId = useUiStore(s => s.focusedMessageId);
  const setFocusedMessageId = useUiStore(s => s.setFocusedMessageId);
  const setContextMenuOpen = useUiStore(s => s.setContextMenuOpen);

  const deleteMessage = useConciergeStore(s => s.deleteMessage);
  const deleteAttachment = useConciergeStore(s => s.deleteAttachment);

  const startEditMessage = useConciergeStore(s => s.startEditMessage);
  const reactToMessage = useConciergeStore(s => s.reactToMessage);
  // Selection mode selectors
  const isSelectionMode = useConciergeStore(s => s.isSelectionMode);
  const getSelectedCount = useConciergeStore(s => s.getSelectedCount);
  const enterSelectionMode = useConciergeStore(s => s.enterSelectionMode);

 const [menuPos, setMenuPos] = React.useState(null); // { mouseX, mouseY }
const menuOpen = Boolean(menuPos);

const openMenuAt = useCallback((x, y) => {
  setMenuPos({ mouseX: x, mouseY: y });
  try { setContextMenuOpen(true); } catch {}
}, [setContextMenuOpen]);

const closeMenu = () => {
  setMenuPos(null);
  try { setContextMenuOpen(false); } catch {}
  // keep halo briefly while menu animates out
  setTimeout(() => { try { setFocusedMessageId(null); } catch {} }, 120);
};

// --- Expose a safe, per-bubble opener so rows (outside the bubble) can trigger the same context menu ---
React.useEffect(() => {
  const id = (msg?._id || msg?.id || msg?.tempId);
  if (!id) return;

  // Handler that only opens *this* bubble's menu
  const handler = (opts) => {
    try {
      const msgId = String(opts?.msgId ?? '');
      if (String(id) !== msgId) return; // ignore for other rows
      try { setFocusedMessageId(id); } catch {}
      const x = opts?.position?.x;
      const y = opts?.position?.y;
      if (typeof x === 'number' && typeof y === 'number') {
        openMenuAt(x, y);
      } else {
        // Fall back to opening near the bubble if coordinates missing
        try {
          const rect = bubbleRef?.current?.getBoundingClientRect?.();
          if (rect) openMenuAt(rect.left + 16, rect.top + 16);
          else openMenuAt(0, 0);
        } catch { openMenuAt(0, 0); }
      }
    } catch {}
  };

  // Keep a registry keyed by message id so multiple bubbles can coexist
  const w = (typeof window !== 'undefined') ? window : {};
  if (!w.__conciergeContextMenuOpenHandlers) {
    try { w.__conciergeContextMenuOpenHandlers = new Map(); } catch {}
  }
  try { w.__conciergeContextMenuOpenHandlers.set(String(id), handler); } catch {}

  // Also expose a convenience function: window.__conciergeContextMenuOpen(opts)
  if (!w.__conciergeContextMenuOpen) {
    try {
      w.__conciergeContextMenuOpen = (opts) => {
        try {
          const k = String(opts?.msgId ?? '');
          const fn = w.__conciergeContextMenuOpenHandlers?.get?.(k);
          if (typeof fn === 'function') fn(opts);
        } catch {}
      };
    } catch {}
  }

  return () => {
    try { w.__conciergeContextMenuOpenHandlers?.delete?.(String(id)); } catch {}
  };
}, [msg?._id, msg?.id, msg?.tempId, openMenuAt, setFocusedMessageId]);


  // Helper to resolve id type for debug/devtools
  const resolveIdInfo = React.useCallback(() => {
    const info = {
      resolved: msg?._id || msg?.id || msg?.tempId,
      hasServerId: !!msg?._id,
      hasId: !!msg?.id,
      hasTempId: !!msg?.tempId,
      type: 'unknown',
    };
    if (msg?._id) info.type = '_id';
    else if (msg?.id) info.type = 'id';
    else if (msg?.tempId) info.type = 'tempId';
    return info;
  }, [msg?._id, msg?.id, msg?.tempId]);


  const messageId = msg?._id || msg?.id || msg?.tempId;
  const isFocused = focusedMessageId === messageId;
  const propertyId = useMemo(
    () => msg?.propertyId || activeThread?.propertyId || null,
    [msg?.propertyId, activeThread?.propertyId]
  );
  const userUid = useMemo(
    () => msg?.userUid || activeThread?.userUid || null,
    [msg?.userUid, activeThread?.userUid]
  );
  // Memoized selection mode flag for this message's context
  const selectionActive = React.useMemo(() => {
    try { return isSelectionMode(propertyId, userUid) || getSelectedCount(propertyId, userUid) > 0; } catch { return false; }
  }, [isSelectionMode, getSelectedCount, propertyId, userUid]);

  const canEdit =
    isHotel && !isSending && attachments.length === 0 && !!propertyId && !!userUid;

  const canDelete =
    isHotel && !isSending && !!propertyId && !!userUid; // show menu only for my messages and not while sending

  const bubbleRef = React.useRef(null);

  const handleContextMenu = (e) => {
  e.preventDefault();
  if (selectionActive && typeof onSelect === 'function') {
    try { onSelect(e, 'toggle'); } catch {}
    return;
  }
  try { setFocusedMessageId(messageId); } catch {}
  try { bubbleRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}

  const x = e.clientX ?? (e.touches?.[0]?.clientX) ?? 0;
  const y = e.clientY ?? (e.touches?.[0]?.clientY) ?? 0;
  openMenuAt(x, y);
};


  // Long-press (mobile) support
  const longPressRef = React.useRef({ t: null, x: 0, y: 0 });
  const onTouchStart = (e) => {
    const touch = e.touches && e.touches[0];
    const x = touch ? touch.clientX : 0;
    const y = touch ? touch.clientY : 0;
    longPressRef.current.x = x;
    longPressRef.current.y = y;
    // Telegram behavior: long-press toggles selection instead of opening menu
    longPressRef.current.t = setTimeout(() => { 
      if (typeof onSelect === 'function') onSelect(undefined, 'toggle'); 
      enterSelectionMode(propertyId, userUid);
    }, 500);
  };
  const clearLong = () => { if (longPressRef.current.t) { clearTimeout(longPressRef.current.t); longPressRef.current.t = null; } };
  const onTouchEnd = clearLong;
  const onTouchMove = clearLong;

  // Memoized menu handlers
  const onReply = () => {
    closeMenu();
    try {
      const snapshot = {
        text: text || (attachments?.length ? '(attachment)' : ''),
        sender: isHotel ? 'Hotel' : 'Guest',
        createdAt: msg?.createdAt || msg?.timestamp || new Date().toISOString(),
      };
      setReplyTarget({
        propertyId,
        userUid,
        messageId: messageId,
        snapshot,
      });
    } catch (e) {
    }
  };
  const onSaveAs = () => { closeMenu(); };
  const onCopyText = async () => {
    closeMenu();
    if (!canCopyText) return;
    const t = text || '';
    try {
      await (navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(t) : Promise.reject());
      
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        
      } catch (err) {
        
      }
    }
  };
  const onEdit = () => {
    closeMenu();
    if (!canEdit) return;
    startEditMessage({ propertyId, userUid, messageId });
    try { document.getElementById('concierge-composer-input')?.focus(); } catch {}
    try { window.dispatchEvent(new CustomEvent('concierge:focusComposer')); } catch {}
  };
  const onPin = () => { closeMenu(); };
  const onForward = () => { closeMenu(); };
  const onSelectMenu = () => { 
    closeMenu(); 
    if (typeof onSelect === 'function') {
      onSelect(undefined, 'toggle'); 
    }
    enterSelectionMode(propertyId, userUid);
  };

  const emojiOptions = useMemo(() => ['👍','❤️','👌','🙏','🔥','🥰'], []);
  const onReact = (emoji) => {
    if (!propertyId || !userUid || !messageId) return;
    
    reactToMessage({ propertyId, userUid, messageId, emoji }); // actor-aware toggle handled in store
  };

  // Normalize reactions for display so counts update immediately (supports map or array)
  const reactionsArr = React.useMemo(() => {
    const raw = msg?.reactions;
    if (!raw) return [];
    // Map shape from store (actor-aware): { [emoji]: { hotel?:bool, guest?:bool, count?:number, actors?:array } }
    if (!Array.isArray(raw) && typeof raw === 'object') {
      return Object.entries(raw).map(([emoji, v]) => {
        const hotel = !!(v && (v.hotel || v.byHotel));
        const guest = !!(v && (v.guest || v.byGuest));
        const countFromActors = Array.isArray(v?.actors) ? v.actors.length : null;
        const count = (countFromActors != null) ? countFromActors : (typeof v?.count === 'number' ? v.count : (hotel ? 1 : 0) + (guest ? 1 : 0));
        return { emoji, count: Number(count) || 0 };
      }).filter((r) => r.emoji && r.emoji !== '0' && r.emoji !== 'null' && r.emoji !== 'undefined');
    }
    // Array shape from server snapshot: [{ emoji, count, hotel?, guest?, actors? }]
    if (Array.isArray(raw)) {
      return raw.map((r) => {
        const hotel = !!(r?.hotel || r?.byHotel);
        const guest = !!(r?.guest || r?.byGuest);
        const countFromActors = Array.isArray(r?.actors) ? r.actors.length : null;
        const count = (countFromActors != null) ? countFromActors : (typeof r?.count === 'number' ? r.count : (hotel ? 1 : 0) + (guest ? 1 : 0));
        return { emoji: String(r?.emoji || ''), count: Number(count) || 0 };
      }).filter((r) => r.emoji);
    }
    return [];
  }, [msg?.reactions]);
  const hasReactions = reactionsArr.length > 0;
  const efShimmerKeyframes = `@keyframes efShimmerMove{0%{background-position:-200% 0;}100%{background-position:200% 0;}}`;


  return (
    <>
      <style>{efShimmerKeyframes}</style>
      <AnimatePresence mode="popLayout">
        <motion.div
          key={String(messageId)}
          layout
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, filter: 'blur(2px)' }}
          transition={{ duration: 0.18 }}
          style={{ width: '100%' }}
        >
          <Box sx={{ width: '100%', px: 1.5, mt: 1, display: 'flex', justifyContent: align }}>
        <Stack component={motion.div} layout direction="row" spacing={1} sx={{ maxWidth: '82%' }}>
          {!isHotel && showAvatar && (
            <Avatar sx={{ width: 28, height: 28, bgcolor: '#e0e0e0' }}>G</Avatar>
          )}

          <Stack spacing={0.5} alignItems={isHotel ? 'flex-end' : 'flex-start'} sx={{ width: '100%' }}>
            <Box
              ref={bubbleRef}
              onContextMenu={handleContextMenu}
              onClick={(e) => {
                if (selectionActive && typeof onSelect === 'function') onSelect(e, 'auto');
              }}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              onTouchMove={onTouchMove}
              data-selected={isSelected ? 'true' : 'false'}
              data-id-type={resolveIdInfo().type}
              data-has-server-id={resolveIdInfo().hasServerId ? 'true' : 'false'}
              data-temp-id={msg?.tempId || ''}
              data-focused={isFocused ? 'true' : 'false'}
              sx={{
                position: 'relative',
                overflow: 'visible',
                background: (isMapOnly || isImageOnly)
                  ? 'transparent'
                  : (isHotel
                      ? 'linear-gradient(180deg, rgba(240,249,255,0.95) 0%, rgba(221,239,247,0.88) 100%)'
                      : 'linear-gradient(180deg, rgba(250,250,250,0.96) 0%, rgba(235,235,235,0.88) 100%)'),
                px: (isMapOnly || isImageOnly) ? 0 : 1.25,
                // pt/pb split for better bottom padding with reactions
                pt: (isMapOnly || isImageOnly) ? 0 : 1.1,
                pb: (isMapOnly || isImageOnly) ? 0 : (hasReactions ? 0.35 : 1.0),
                borderTopLeftRadius: (isMapOnly || isImageOnly) ? 0 : corner,
                borderTopRightRadius: (isMapOnly || isImageOnly) ? 0 : corner,
                borderBottomLeftRadius: (isMapOnly || isImageOnly) ? 0 : (isHotel ? corner : 8),
                borderBottomRightRadius: (isMapOnly || isImageOnly) ? 0 : (isHotel ? 8 : corner),
                outline: 'none',
                boxShadow: (isMapOnly || isImageOnly) ? 'none' : (isFocused ? '0 8px 22px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.08)' : (isSelected ? '0 6px 18px rgba(96,165,250,0.28), 0 1px 4px rgba(0,0,0,0.08)' : shadow)),
                transition: 'transform 150ms ease, box-shadow 150ms ease, outline 80ms ease',
                opacity: isDeleting ? 0.6 : 1,
                transform: isFocused ? 'translateY(-2px) scale(1.01)' : (isSelected ? 'translateY(-1px)' : (isDeleting ? 'scale(0.98)' : 'translateY(0)')),
                width: '100%',
                alignSelf: isHotel ? 'flex-end' : 'flex-start',
                ml: isHotel ? 6 : 0,
                mr: isHotel ? 0 : 6,
                // continuous shimmer overlay for pending states
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -1,
                  borderRadius: (isMapOnly || isImageOnly) ? 0 : corner,
                  background: 'linear-gradient(115deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 12%, rgba(255,255,255,0.55) 25%, rgba(255,255,255,0.35) 38%, rgba(255,255,255,0) 50%)',
                  backgroundSize: '200% 100%',
                  animation: (isSending || isUpdating) ? 'efShimmerMove 1.4s linear infinite' : 'none',
                  pointerEvents: 'none',
                  mixBlendMode: 'soft-light',
                },
                // hover/active shimmer overlay
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -1,
                  borderRadius: (isMapOnly || isImageOnly) ? 0 : corner,
                  background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 15%, rgba(255,255,255,0.65) 30%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0) 60%)',
                  transform: 'translateX(-140%)',
                  transition: 'transform 650ms ease',
                  pointerEvents: 'none',
                  mixBlendMode: 'soft-light',
                },
                '&:hover::after, &[data-focused="true"]::after': {
                  transform: 'translateX(140%)',
                },
              }}
            >
              {/* Deleting visual overlay */}
              {isDeleting && (
                <>
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0.35 }}
                    animate={{ scale: [0.95, 1.02, 0.98, 1], opacity: [0.35, 0.25, 0.2, 0.18] }}
                    transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
                    style={{
                      position: 'absolute',
                      inset: -4,
                      borderRadius: 20,
                      border: '2px solid #ef4444',
                      pointerEvents: 'none',
                    }}
                  />
                  <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.35)' }}>
                      <CircularProgress size={14} sx={{ color: '#fff' }} />
                      <Typography variant="caption" sx={{ color: '#fff' }}>Deleting…</Typography>
                    </Stack>
                  </Box>
                </>
              )}
              {msg?.replySnapshot && (
                <Box
                  onClick={() => msg?.replyTo && onQuotedClick && onQuotedClick(String(msg.replyTo))}
                  sx={{
                    borderLeft: isHotel ? '3px solid #6AA7FF' : '3px solid #B3B3B3',
                    pl: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    background: isHotel
                      ? 'linear-gradient(180deg, rgba(232,244,255,0.95) 0%, rgba(216,234,252,0.85) 100%)'
                      : 'linear-gradient(180deg, rgba(250,250,250,0.96) 0%, rgba(238,238,238,0.88) 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.04)',
                    cursor: msg?.replyTo ? 'pointer' : 'default',
                    '&:hover': msg?.replyTo ? { filter: 'brightness(0.98)' } : undefined,
                  }}
                  title={msg?.replyTo ? 'Jump to replied message' : undefined}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                    {msg.replySnapshot.sender}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#444' }} noWrap>
                    {msg.replySnapshot.text}
                  </Typography>
                </Box>
              )}
              {parsedMap && (
                <Box sx={{ mb: (isMapOnly || isImageOnly) ? 0 : 0.5 }}>
                  <MapAttachment
                    att={{ name: parsedMap.title, title: parsedMap.title, address: parsedMap.address, lat: parsedMap.lat, lng: parsedMap.lng, url: parsedMap.url, type: 'location' }}
                    msg={msg}
                    isHotel={isHotel}
                    isBusy={isBusy}
                    onOpenMap={openMapModal}
                  />
                </Box>
              )}

              {attachments.length > 0 && (
                <Box
                  sx={{
                    mb: text ? 0.75 : 0,
                    p: isImageOnly ? 0 : undefined,
                    bgcolor: isImageOnly ? 'transparent' : undefined,
                    boxShadow: isImageOnly ? 'none' : undefined,
                  }}
                >
                  {showImageGrid ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {attachments.map((a, idx) => (
                        <Attachment key={`${a.url || idx}-${idx}`} att={a} msg={msg} isHotel={isHotel} grid onOpen={openLightbox} isBusy={isBusy} bare={isImageOnly} onOpenMap={openMapModal} />
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0, m: 0 }}>
                      {attachments.map((a, idx) => (
                        <Attachment key={`${a.url || idx}-${idx}`} att={a} msg={msg} isHotel={isHotel} onOpen={openLightbox} isBusy={isBusy} bare={isImageOnly} onOpenMap={openMapModal} />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {!parsedMap && !(isMapOnly || isImageOnly) && (text && text.trim().length > 0) && (
                <Typography
                  variant="body2"
                  data-msg-id={msg?.id || msg?._id || msg?.tempId}
                  sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap', color: bubbleText }}
                >
                  {text}
                </Typography>
              )}
              {/* Inline, minimalist reactions inside the bubble */}
              {reactionsArr.length > 0 && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    gap: 0.25,
                    mt: 0.15,
                    mb: '-6px',
                    alignSelf: isHotel ? 'flex-end' : 'flex-start',
                    pointerEvents: 'auto',
                    // subtle lift from background
                    '& > .rx': {
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      padding: '2px 6px',
                      borderRadius: 999,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      backgroundColor: isHotel ? 'rgba(255,255,255,0.85)' : 'rgba(17,24,39,0.06)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'transform 80ms ease, background-color 120ms ease',
                      '&:hover': { backgroundColor: isHotel ? 'rgba(255,255,255,0.95)' : 'rgba(17,24,39,0.10)' },
                      '&:active': { transform: 'scale(0.98)' },
                    }
                  }}
                >
                  {reactionsArr.map((r) => {
                    const displayEmoji = r.emoji;
                    const entry = (msg?.reactions && !Array.isArray(msg.reactions) ? msg.reactions[displayEmoji] : null) || {};
                    const mine = Array.isArray(entry?.actors) ? entry.actors.some(a => a && (a.role === 'staff')) : !!entry?.hotel;
                    const count = Number(r.count) || 0;
                    return (
                      <Box
                        key={displayEmoji}
                        className="rx"
                        onClick={() => displayEmoji && reactToMessage({ propertyId, userUid, messageId, emoji: displayEmoji })}
                        title={mine ? `You reacted ${displayEmoji}` : `React ${displayEmoji}`}
                        sx={{
                          backgroundColor: mine ? (isHotel ? 'rgba(255,255,255,0.65)' : 'rgba(37,99,235,0.08)') : undefined,
                          borderColor: mine ? 'rgba(37,99,235,0.35)' : 'rgba(0,0,0,0.05)',
                        }}
                      >
                        <span style={{ fontSize: 13, lineHeight: '14px' }}>{displayEmoji}</span>
                        {count > 1 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: bubbleText,
                              fontSize: 12,
                              fontWeight: 500,
                              opacity: 0.85,
                              lineHeight: '14px'
                            }}
                          >
                            {count}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.35,
              alignSelf: isHotel ? 'flex-end' : 'flex-start',
              mr: isHotel ? 0 : 6,
              ml: isHotel ? 6 : 0,
            }}>
              <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: 11 }} title={timeTitle}>{time}</Typography>
              {/* Seen read-receipt for my (hotel) outgoing messages */}
              {isSeenReceipt && (
                <span
                  aria-label="Seen"
                  title="Seen"
                  style={{ display: 'inline-flex', alignItems: 'center', opacity: 0.9 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" style={{ display: 'block' }}>
                    <path d="M6 12l3 3 7-7" stroke="#4B5563" strokeWidth="2" fill="none"/>
                    <path d="M4 13l3 3" stroke="#4B5563" strokeWidth="2" fill="none"/>
                  </svg>
                </span>
              )}
              {msg?.editedAt && (
                <Typography
                  variant="caption"
                  sx={{ color: '#9CA3AF', fontSize: 11 }}
                  title={`Edited ${formatTime(msg.editedAt)}`}
                >
                  (edited)
                </Typography>
              )}
              {isHotel && isSending && (
                <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: 11 }}>sending…</Typography>
              )}
              {isHotel && isFailed && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#DC2626', fontSize: 11 }}>failed</Typography>
                  <MuiLink component="button" type="button" onClick={() => retrySend({ propertyId: msg.propertyId, userUid: msg.userUid, tempId: msg.tempId })} sx={{ fontSize: 11, fontWeight: 700, color: '#2563EB', textDecoration: 'none' }}>
                    Retry
                  </MuiLink>
                </Box>
              )}
            </Box>
            <ContextMenu
              open={menuOpen}
              anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
              onClose={closeMenu}
              onReply={onReply}
              onCopyText={onCopyText}
              onSaveAs={onSaveAs}
              onEdit={onEdit}
              onPin={onPin}
              onForward={onForward}
              onSelect={onSelectMenu}
              onDelete={() => {
                closeMenu();
                if (canDelete) {
                  const idInfo = resolveIdInfo();
                  try {
                  } catch {}
                  deleteMessage({ propertyId, userUid, messageId, hardDelete: true, deleteAttachments: true });
                }
              }}
              canCopyText={canCopyText}
              canEdit={canEdit}
              canDelete={canDelete}
              hasSingleImage={hasSingleImage}
              showEmojiBar={Boolean(menuOpen && menuPos)}
              emojiOptions={emojiOptions}
              onReact={(e) => onReact(e)}
            />
          </Stack>

          {isHotel && showAvatar && (
            <Avatar sx={{ width: 28, height: 28, bgcolor: '#e3f2fd' }}>H</Avatar>
          )}
        </Stack>
          </Box>
        </motion.div>
      </AnimatePresence>

      {/* Lightbox */}
      <Dialog open={!!lightboxSrc} onClose={closeLightbox} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#000' }}>
          <IconButton onClick={closeLightbox} size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          {lightboxSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
              <img src={lightboxSrc} alt="preview" style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block' }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <MapViewModal
        open={!!mapModal}
        onClose={closeMapModal}
        title={mapModal?.title || mapModal?.name}
        address={mapModal?.address}
        lat={mapModal?.lat ?? mapModal?.latitude}
        lng={mapModal?.lng ?? mapModal?.longitude}
        latitude={mapModal?.lat ?? mapModal?.latitude}
        longitude={mapModal?.lng ?? mapModal?.longitude}
        link={mapModal?.url}
        mapsUrl={mapModal?.url}
        gKey={gKey}
      />
    </>
  );
}

function Attachment({ att, msg, isHotel, grid = false, onOpen, isBusy, bare = false, onOpenMap }) {
  const url = att?.url || (att?.publicId ? `https://res.cloudinary.com/demo/image/upload/${att.publicId}` : null);
  const type = (att?.type || '').toLowerCase();
  const name = att?.name || 'Attachment';
  const publicId = att?.publicId;
  // Inline view (short-lived) generated by backend at upload time
  const inlineUrl = att?.inlineUrl || null;
  const inlineExp = Number(att?.inlineExpiresAt || 0);
  const canUseInline = !!inlineUrl && (!inlineExp || (Date.now() / 1000) < inlineExp);

  // Telegram-like download state hooks MUST be at top-level
  const [dlState, setDlState] = React.useState('idle'); // 'idle' | 'downloading' | 'ready'
  const [dlProgress, setDlProgress] = React.useState(0);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!url) { if (mounted) setDlState('idle'); return; }
      const has = await cacheHas(url);
      
      if (mounted) setDlState(has ? 'ready' : 'idle');
    })();
    return () => { mounted = false; };
  }, [url]);

  // If this is a map/location attachment, render that immediately (may not have a URL)
  if (isMapAtt(att)) {
    return <MapAttachment att={att} msg={msg} isHotel={isHotel} isBusy={isBusy} onOpenMap={onOpenMap} />;
  }

  // Render placeholder when url is missing
  if (!url) {
    return (
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconFileText size={18} strokeWidth={2} color="#666" />
        <Typography variant="body2" sx={{ color: '#444' }}>{att?.name || 'Attachment pending'}</Typography>
        {isBusy && <CircularProgress size={14} sx={{ color: '#999' }} />}
      </Box>
    );
  }

  const nameLower = (att?.name || '').toLowerCase();
  const urlLower = (url || '').toLowerCase();
  const isPdf = type === 'application/pdf' || nameLower.endsWith('.pdf') || /\.pdf($|\?)/i.test(urlLower);
  const isImageType = type.startsWith('image/') || type === 'image';
  const isImageExt = /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(urlLower) || /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(nameLower);
  const isImage = !isPdf && (isImageType || isImageExt || (!!att?.thumbnailUrl && !isPdf));
  const { deleteAttachment: removeAtt } = useConciergeStore.getState();

  if (isImage) {
    const src = att?.thumbnailUrl || url;
    if (grid) {
      // Small square tile for gallery look
      return (
        <Box
          onClick={() => { onOpen && onOpen(att.url); }}
          sx={{
            position: 'relative',
            width: 96,
            height: 96,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.10)',
            boxShadow: bare ? '0 1px 3px rgba(0,0,0,0.08)' : '0 6px 16px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)',
            cursor: 'pointer',
            '&:hover': { opacity: 0.95 },
            pointerEvents: isBusy ? 'none' : 'auto',
          }}
          aria-busy={isBusy ? 'true' : 'false'}
        >
          <img src={src} alt={name} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
          {isBusy && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            </Box>
          )}
        </Box>
      );
    }
    // Single image: show responsive with rounded corners
    return (
      <Box
        onClick={() => { onOpen && onOpen(att.url); }}
        sx={{
          position: 'relative',
          borderRadius: bare ? '6px' : 2,
          overflow: 'hidden',
          border: bare ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(0,0,0,0.12)',
          boxShadow: bare
            ? '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)'
            : '0 8px 24px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)',
          maxHeight: 480,
          cursor: 'pointer',
          '&:hover': { opacity: 0.95 },
          pointerEvents: isBusy ? 'none' : 'auto',
          m: bare ? 0 : undefined,
        }}
        aria-busy={isBusy ? 'true' : 'false'}
      >
        <img src={src} alt={name} style={{ display: 'block', width: '100%', height: 'auto' }} />
        {isBusy && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          </Box>
        )}
      </Box>
    );
  }

  const cardBg = isHotel ? 'rgba(255,255,255,0.9)' : '#FFFFFF';
  const decodedName = decodeURIComponent(name || 'Attachment');
  const ext = (decodedName.split('.').pop() || '').toUpperCase() || 'FILE';
  const rawSize = att?.fileSizeLabel ?? att?.sizeLabel ?? att?.size;
  const sizeLabel = formatBytesHuman(rawSize) || '0 B';


  const openInline = () => {
    if (!inlineUrl) return;
    const w = window.open(inlineUrl, '_blank', 'noopener');
    if (!w) {
      // fallback if popup blocked
      const a = document.createElement('a');
      a.href = inlineUrl; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 2000);
    }
  };

  const downloadFile = async () => {
    if (dlState === 'downloading') return;
    // If the message is mine (isHotel === true) and we have a fresh inline URL, open that directly
    if (isHotel && canUseInline) {
      openInline();
      // Mark as ready so the next click uses the inline viewer
      setDlState('ready');
      setDlProgress(100);
      return;
    }
    setDlState('downloading');
    setDlProgress(0);
    try {
      // Include auth for same-origin/protected file endpoints
      const isSameOrigin = (() => {
        try {
          if (!/^https?:/i.test(url)) return true; // relative path
          const u = new URL(url, window.location.href);
          return u.origin === window.location.origin;
        } catch { return false; }
      })();
      const state = (typeof useConciergeStore !== 'undefined' && useConciergeStore.getState ? useConciergeStore.getState() : {}) || {};
      const token =
        (state.auth && (state.auth.accessToken || state.auth.token)) ||
        (state.session && (state.session.accessToken || state.session.token)) ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('ef_token') : null);
      const headers = new Headers();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      
      let resp = await fetch(url, {
        credentials: isSameOrigin ? 'include' : 'omit',
        headers,
        redirect: 'follow',
      });
      if (!resp.ok) {
        if (resp.status === 401 || resp.status === 403) {
          
          // 1) retry without headers (CDN case)
          const retry1 = await fetch(url, {
            credentials: isSameOrigin ? 'include' : 'omit',
            redirect: 'follow',
          });
          if (retry1.ok) {
            resp = retry1;
          } else {
            // 2) If still unauthorized and looks like Cloudinary, try to obtain a signed URL from backend
            const looksCloudinary = /https?:\/\/res\.cloudinary\.com\//i.test(url);
            if (looksCloudinary && publicId) {
              try {
                // Prefer filename + stored resource type over path guesses
                const pathType =
                  urlLower.includes('/image/') ? 'image' :
                  urlLower.includes('/raw/') ? 'raw' :
                  urlLower.includes('/video/') ? 'video' : null;

                // Extension-based inference
                const extMatchUrl = urlLower.match(/\.([a-z0-9]{2,6})(?:$|\?)/);
                const extMatchName = nameLower.match(/\.([a-z0-9]{2,6})$/);
                const ext = (extMatchName && extMatchName[1]) || (extMatchUrl && extMatchUrl[1]) || '';
                const docExts = new Set(['pdf','zip','txt','csv','doc','docx','ppt','pptx','xls','xlsx']);

                // Start with server-provided storedResourceType when available
                let rt = (att && typeof att.storedResourceType === 'string' && ['image','raw','video'].includes(att.storedResourceType))
                  ? att.storedResourceType
                  : (docExts.has(ext) ? 'raw'
                     : (pathType || (type.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(urlLower) ? 'video' : (isImageType ? 'image' : 'image'))));

                // If it's any document type by extension, force RAW (never video)
                if (docExts.has(ext) || isPdf) rt = 'raw';

                // Try order: for docs => raw -> image; for image => image -> raw; for video => video only
                const tryOrder = rt === 'raw' ? ['raw','image'] : (rt === 'image' ? ['image','raw'] : ['video']);
                

                // Delivery: prefer authenticated only
                const deliveryTryOrder = ['authenticated'];
                

                let success = false;
                for (const candidate of tryOrder) {
                  for (const delivery of deliveryTryOrder) {
                    
                    try {
                      const signedUrl = await getSignedAttachmentUrlFor(att, {
                        ttlSeconds: 300,
                        forceResourceType: candidate,
                        format: ext || (isPdf ? 'pdf' : undefined),
                      });
                      
                      const r2 = await fetch(signedUrl, { credentials: 'omit', redirect: 'follow' });
                      
                      if (r2.ok) {
                        resp = r2;
                        success = true;
                        
                        break;
                      } else {
                        
                      }
                    } catch (e) {
                      
                    }
                  }
                  if (success) break;
                }
                if (!success) {
                  
                  throw new Error('HTTP 404');
                }
              } catch (e) {
                
                throw new Error(`HTTP ${retry1.status}`);
              }
            } else {
              throw new Error(`HTTP ${retry1.status}`);
            }
          }
        } else {
          throw new Error(`HTTP ${resp.status}`);
        }
      }
      // Stream to support progress when possible
      const total = Number(resp.headers.get('Content-Length') || 0);
      if (resp.body && total > 0 && 'ReadableStream' in window) {
        const reader = resp.body.getReader();
        const chunks = [];
        let loaded = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.byteLength;
          setDlProgress(Math.min(100, Math.round((loaded / total) * 100)));
        }
        const blob = new Blob(chunks);
        // --- PATCH: Use improved content-type + debug log
        const serverType = resp.headers.get('Content-Type');
        const guessType = inferMimeFromName(decodedName);
        const contentType = serverType && serverType !== 'application/octet-stream' ? serverType : guessType;
        const responseForCache = new Response(blob, { headers: { 'Content-Type': contentType } });
        
        await cachePut(url, responseForCache);
      } else {
        const blob = await resp.blob();
        // --- PATCH: Use improved content-type + debug log
        const serverType = resp.headers.get('Content-Type');
        const guessType = inferMimeFromName(decodedName);
        const contentType = serverType && serverType !== 'application/octet-stream' ? serverType : guessType;
        const responseForCache = new Response(blob, { headers: { 'Content-Type': contentType } });
        
        await cachePut(url, responseForCache);
      }
      setDlState('ready');
      setDlProgress(100);
    } catch (e) {
      setDlState('idle');
    }
  };

  // --- PATCH: Inline viewer with iframe for inline display and navigation race avoidance
  const openDownloaded = async () => {
    if (dlState !== 'ready') return;
    // Synchronously open a tab to keep user gesture
    const tab = window.open('', '_blank');
    if (!tab) {
    }
    const { blobUrl, contentType } = await cacheOpenBlobUrlWithType(url, decodedName);
    
    if (!blobUrl) {
      // Fallback to remote URL
      if (tab) tab.location.href = url; else window.open(url, '_blank');
      return;
    }

    // Write a minimal viewer shell so Chrome treats it as inline content
    const title = decodedName.replace(/"/g, '');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <meta name="viewport" content="width=device-width,initial-scale=1">
      <style>html,body{height:100%;margin:0}iframe{border:0;width:100%;height:100%}</style>
    </head><body>
      <iframe src="${blobUrl}" allow="autoplay"></iframe>
    </body></html>`;
    try {
      if (tab && tab.document) {
        
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
      } else {
        // Last-resort fallback
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch {} a.remove(); }, 6000);
        return;
      }
    } finally {
      // Revoke after a bit; the iframe has already loaded it
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch {} }, 8000);
    }

    
  };

  return (
    <Box sx={{
      width: BUBBLE_WIDTH,
      borderRadius: 2,
      p: 1.25,
      bgcolor: cardBg,
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
      opacity: isBusy ? 0.6 : 1,
      pointerEvents: isBusy ? 'none' : 'auto',
    }}>
      {/* Top row: single leading icon (Download → spinner → File) + filename + type pill when ready */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {/* Leading icon is the only action: downloads when idle, opens when ready */}
          <Box
            onClick={dlState === 'ready' ? openDownloaded : (dlState === 'idle' ? (isHotel && canUseInline ? openInline : downloadFile) : undefined)}
            sx={{ cursor: dlState === 'downloading' ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }}
            title={dlState === 'ready' ? 'Open' : (dlState === 'idle' ? (isHotel && canUseInline ? 'View' : 'Download') : 'Downloading…')}
          >
            {dlState === 'downloading' ? (
              <CircularProgress size={16} sx={{ color: '#2563EB' }} />
            ) : dlState === 'ready' ? (
              <IconFileText size={18} strokeWidth={2} color="#2F3A49" />
            ) : (
              <IconDownload size={18} strokeWidth={2} color="#2563EB" />
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {decodedName.length > 80 ? decodedName.slice(0, 77) + '…' : decodedName}
          </Typography>
        </Box>

        {/* Right side: show type pill only when ready */}
        {dlState === 'ready' && (
          <Box onClick={isHotel && canUseInline ? openInline : openDownloaded} title={(isHotel && canUseInline) ? `View ${ext} (inline)` : `Open ${ext}`} sx={{
            cursor: 'pointer', fontSize: 11, fontWeight: 700, px: 1, py: 0.25, borderRadius: 999,
            bgcolor: '#111827', color: '#fff', lineHeight: '14px'
          }}>
            {ext}
          </Box>
        )}
      </Box>

      {/* Bottom row: size only */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 0.75 }}>
        <Typography variant="caption" sx={{ color: '#374151' }}>{sizeLabel}</Typography>
      </Box>
    </Box>
  );
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}